import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import { Container } from "react-bootstrap";
import { getReferer, signUp, storeformData } from "../../../store/actions/authActions";
import { clearNotifications } from "../../../store/actions/notificationsActions";
import RegistrationGuide from "../../utilities/RegistrationGuide";
import PaymentDetails from "./PaymentDetails";
import EmploymentDetails from "./EmploymentDetails";
import PersonalDetails from "./PersonalDetails.jsx";
import SummaryStep from "./SummaryStep.jsx";
import { ProgressStepper } from "../../utilities/StepperComponents";

const Registration = () => {
	const dispatch = useDispatch();
	const { errors, success, data, referer } = useSelector((state) => ({
		errors: state.notification.errors,
		success: state.notification.success,
		data: state.instruction.registration,
		referer: state.auth.referer,
	}));

	const [formStep, setFormStep] = useState(1);
	const [regGuideModal, setRegGuideModal] = useState(false);
	const [localState, setLocalState] = useState({
		tellerName: "",
		tellerRefNum: "",
		bankName: "",
		paymentDate: "",
		amountPaid: "",
		organisationName: "",
		rank: "",
		gradeLevel: "",
		retirementDate: "",
		step: "",
		campusName: "",
		assumptionDate: "",
		ippisNum: "",
		salaryStructure: "",
		faculty: "",
		department: "",
		staffNum: "",
		fullName: "",
		email: "",
		homeAddress: "",
		signature: "",
		signatureURL: "",
		passport: "",
		passportURL: "",
		phone: "",
		password: "",
		confirmPassword: "",
		birthDate: "",
		category: "",
		title: "",
		gender: "",
		nin: "",
		terms: false,
		refererId: "",
		refererName: "",
	});

	const [formErrors, setFormErrors] = useState({});
	const [disableButton, setDisableButton] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		const queryString = window.location.search;
		const urlParam = new URLSearchParams(queryString);
		const ref = urlParam.get("ref");
		if (ref) {
			dispatch(getReferer(ref));
		}
	}, [dispatch]);

	useEffect(() => {
		if (referer?.user) {
			setLocalState(prev => ({
				...prev,
				refererId: referer.user._id,
				refererName: referer.user.name
			}));
		}
	}, [referer]);

	useEffect(() => {
		if (errors?.message) {
			toast.error(errors.message);
			setIsSubmitting(false);
			dispatch(clearNotifications());
		}
		if (success?.message) {
			showRedirectAlert();
			setIsSubmitting(false);
			dispatch(clearNotifications());
		}
	}, [errors, success, dispatch]);

	const getFields = (step) => {
		const {
			tellerName, tellerRefNum, bankName, paymentDate, amountPaid,
			organisationName, rank, gradeLevel, retirementDate, step: s,
			campusName, assumptionDate, ippisNum, salaryStructure,
			faculty, department, staffNum, category,
			fullName, email, homeAddress, phone, birthDate, title, gender, nin,
			password, confirmPassword, terms, passport, signature
		} = localState;

		switch (step) {
			case 1:
				return { tellerName, tellerRefNum, bankName, paymentDate, amountPaid };
			case 2:
				return { organisationName, rank, gradeLevel, retirementDate, step: s, campusName, assumptionDate, ippisNum, salaryStructure, faculty, department, staffNum, category };
			case 3:
				return { fullName, email, homeAddress, phone, birthDate, title, gender, password, confirmPassword, terms, passport, signature, nin };
			default:
				return {};
		}
	};

	const checkValidation = (step) => {
		const fields = getFields(step);
		const fieldNames = Object.keys(fields);
		const isValid = fieldNames.every(name => {
			const val = localState[name];
			if (name === 'terms') return val === true;
			if (name === 'phone') return /^0\d{10}$/.test(String(val || ""));
			if (name === 'organisationName' || name === 'title') {
				return val && val.toString().trim().length > 0 && val !== "Others";
			}
			return val && val.toString().trim().length > 0;
		});
		setDisableButton(!isValid);
	};

	useEffect(() => {
		checkValidation(formStep);
	}, [localState, formStep]);

	const handleChange = (event) => {
		const { name, value, type, checked, files } = event.target;

		if (files) {
			const file = files[0];
			setLocalState(prev => ({
				...prev,
				[name]: file,
				[`${name}URL`]: URL.createObjectURL(file)
			}));
		} else if (type === "checkbox") {
			setLocalState(prev => ({ ...prev, [name]: checked }));
		} else {
			setLocalState(prev => ({ ...prev, [name]: value }));
		}

		if (formErrors[name]) {
			setFormErrors(prev => ({ ...prev, [name]: null }));
		}
	};

	const nextStep = () => {
		setFormStep(prev => prev + 1);
		dispatch(storeformData(localState));
	};

	const prevStep = () => {
		setFormStep(prev => prev - 1);
	};

	const findErrors = () => {
		const newErrors = {};
		const requiredFields = [
			"tellerName", "tellerRefNum", "bankName", "paymentDate", "amountPaid",
			"organisationName", "rank", "gradeLevel", "retirementDate", "step",
			"campusName", "assumptionDate", "ippisNum", "salaryStructure",
			"faculty", "department", "staffNum", "fullName", "email",
			"homeAddress", "signature", "passport", "password", "confirmPassword",
			"birthDate", "category", "phone", "title", "gender", "nin"
		];

		requiredFields.forEach(field => {
			if (!localState[field] || localState[field] === "") {
				newErrors[field] = "cannot be blank!";
			}
		});

		if (localState.nin && (!/^\d{11}$/.test(localState.nin))) {
			newErrors.nin = "NIN must be exactly 11 digits";
		}

		if (localState.phone && !/^0\d{10}$/.test(localState.phone)) {
			newErrors.phone = "Phone number must be exactly 11 digits and start with 0";
		}

		if (localState.amountPaid !== "2000") {
			newErrors.amountPaid = "amount should be 2000";
		}

		if (!localState.terms) {
			newErrors.terms = "please accept our terms";
		}

		return newErrors;
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const errorsFound = findErrors();
		if (Object.keys(errorsFound).length > 0) {
			setFormErrors(errorsFound);
			toast.error("Please fill all required fields correctly.");
			setIsSubmitting(false);
		} else {
			setIsSubmitting(true);
			const { organisationName, category } = localState;
			let firstNumIndex = localState.staffNum.search(/\d/);
			let sliced = localState.staffNum.slice(firstNumIndex);
			let staffNumber = localState.staffNum;

			if (organisationName === "University of Nigeria") {
				staffNumber = category === "Senior" ? `UNP/SS. ${sliced}` : `UNP/JS. ${sliced}`;
			}

			const formDataToSubmit = new FormData();
			Object.keys(localState).forEach(key => {
				if (!key.endsWith("URL") && key !== "refererName") {
					formDataToSubmit.append(key === "fullName" ? "fullname" : key === "amountPaid" ? "amount" : key === "assumptionDate" ? "assumptionOfdutyDate" : key === "confirmPassword" ? "confirmpassword" : key === "tellerName" ? "tellername" : key === "bankName" ? "bankname" : key, localState[key]);
				}
			});
			// Overwrite staffNum with formatted version
			formDataToSubmit.set("staffNum", staffNumber);

			dispatch(signUp(formDataToSubmit));
		}
	};

	const showRedirectAlert = () => {
		Swal.fire({
			title: 'Registration successful',
			text: 'Click "Okay" to continue to login',
			icon: 'success',
			confirmButtonText: 'Okay',
			allowOutsideClick: false,
		}).then(() => {
			window.location.href = 'https://dashboard.lmcsnigltd.org.ng/';
		});
	};

	const steps = [
		{ number: 1, title: 'Payment' },
		{ number: 2, title: 'Employment' },
		{ number: 3, title: 'Personal' },
		{ number: 4, title: 'Summary' }
	];

	return (
		<div className="auth-page-wrapper">
			<div className="auth-card">
				<div className="auth-sidebar">
					<div>
						<Link to="/" className="mb-4 d-inline-block">
							<button className="btn btn-modern btn-modern-outline" style={{ color: '#fff', borderColor: '#fff' }}>
								← Home
							</button>
						</Link>
						<h2>Join the Cooperative</h2>
						<p>Experience a modern way of managing your cooperative membership and benefits.</p>
					</div>

					<div className="mt-auto">
						<p className="mb-2">Already a member?</p>
						<a href="https://dashboard.lmcsnigltd.org.ng" className="btn btn-modern btn-modern-outline w-100" style={{ color: '#fff', borderColor: '#fff' }}>
							Sign In Instead
						</a>
						<button className="btn btn-modern w-100 mt-3" onClick={() => setRegGuideModal(true)} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
							Registration Guide
						</button>
					</div>
				</div>

				<div className="auth-form-container">
					<div className="reg-header">
						<h3>Member Registration</h3>
						<p>Fill in your details to get started with your membership.</p>
					</div>

					<div className="stepper-progress-container">
						<ProgressStepper steps={steps} currentStep={formStep} />
					</div>

					<div className="form-content">
						{formStep === 1 && (
							<PaymentDetails
								nextStep={nextStep}
								handleChange={handleChange}
								error={formErrors}
								disableButton={disableButton}
								data={localState}
							/>
						)}
						{formStep === 2 && (
							<EmploymentDetails
								nextStep={nextStep}
								prevStep={prevStep}
								handleChange={handleChange}
								error={formErrors}
								disableButton={disableButton}
								data={localState}
							/>
						)}
						{formStep === 3 && (
							<PersonalDetails
								nextStep={nextStep}
								prevStep={prevStep}
								handleChange={handleChange}
								handleSubmit={nextStep}
								submitError={errors}
								error={formErrors}
								terms={localState.terms}
								refererName={localState.refererName}
								disableButton={disableButton}
								passportURL={localState.passportURL}
								signatureURL={localState.signatureURL}
								data={localState}
							/>
						)}
						{formStep === 4 && (
							<SummaryStep
								data={localState}
								passportURL={localState.passportURL}
								signatureURL={localState.signatureURL}
								prevStep={prevStep}
								handleSubmit={handleSubmit}
								isSubmitting={isSubmitting}
							/>
						)}
					</div>
				</div>
			</div>

			<ToastContainer position="top-center" />
			<RegistrationGuide
				regGuideModal={regGuideModal}
				handleCloseModal={() => setRegGuideModal(false)}
			/>
		</div>
	);
};

export default Registration;
